import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import Navigation from './components/Navigation';
import Login from './components/Login';
import AgentList from './components/AgentList';
import CallReportList from './components/CallReportList';
import CallReportForm from './components/CallReportForm';
import CallDurationPage from './components/CallDurationPage';
import AgentForm from './components/AgentForm';
import AgentProfile from './components/AgentProfile';
import AgentRuleBasedCall from './components/AgentRuleBasedCall';
import CallStatistics from './components/CallStatistics';
import Settings from './components/Settings';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
});

const PrivateRoute = ({ children, roles, agentId }) => {
  const user = JSON.parse(localStorage.getItem('user'));
  
  if (!user) {
    return <Navigate to="/login" />;
  }

  // Check if user has required role
  if (roles && !roles.includes(user.role)) {
    return <Navigate to="/profile" />;
  }

  // For agent role, check if they're trying to access their own data
  if (user.role === 'agent' && agentId && agentId !== user.customAgentId) {
    return <Navigate to="/profile" />;
  }

  return children;
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Navigation />
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* Agent Profile Route */}
        <Route
          path="/profile"
          element={
            <PrivateRoute roles={['agent']}>
              <AgentProfile />
            </PrivateRoute>
          }
        />

        {/* Admin Only Routes */}
        <Route
          path="/agents"
          element={
            <PrivateRoute roles={['admin']}>
              <AgentList />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents/new"
          element={
            <PrivateRoute roles={['admin']}>
              <AgentForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents/:id/edit"
          element={
            <PrivateRoute roles={['admin']}>
              <AgentForm />
            </PrivateRoute>
          }
        />

        {/* Call Reports Routes */}
        <Route
          path="/agents/:customAgentId/call-reports"
          element={
            <PrivateRoute>
              <CallReportList />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents/:customAgentId/call-reports/new"
          element={
            <PrivateRoute roles={['admin']}>
              <CallReportForm />
            </PrivateRoute>
          }
        />
        <Route
          path="/agents/:customAgentId/call-duration"
          element={
            <PrivateRoute>
              <CallDurationPage />
            </PrivateRoute>
          }
        />

        {/* Call Statistics Route */}
        <Route
          path="/call-statistics"
          element={
            <PrivateRoute>
              <CallStatistics />
            </PrivateRoute>
          }
        />

        {/* Agent Rule Based Call Route */}
        <Route
          path="/agent-rule-based-call/:agentCustomId"
          element={
            <PrivateRoute>
              <AgentRuleBasedCall />
            </PrivateRoute>
          }
        />

        {/* Settings Route (Admin Only) */}
        <Route
          path="/settings"
          element={
            <PrivateRoute roles={['admin']}>
              <Settings />
            </PrivateRoute>
          }
        />

        {/* Default Route */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              {({ user }) => (
                user.role === 'admin' 
                  ? <Navigate to="/agents" replace />
                  : <Navigate to="/profile" replace />
              )}
            </PrivateRoute>
          }
        />
      </Routes>
    </ThemeProvider>
  );
}

export default App; 